import { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import { Window } from '@progress/kendo-react-dialogs';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { useLocalization } from '@progress/kendo-react-intl';
import { useQuery } from 'react-query';
import { enMessages } from '../../../../../messages/en-US';
import { EventFilter } from '../../../../Events/Utilities/EventsApiService';
import healthDashboardApiService from '../../../Health/Utilities/HealthDashboardApiService';
import GatewayHealthDetails from '../../../Health/Components/Gateways/GatewayHealthDetails';
import LoadingSpinner from '../../../../Shared/Components/LoadingSpinner';
import { ReactQueryKeys } from '../../../../../Constants/ClientRoutingConstants';
import DeviceInfoReadDto from '../../../../../Models/Dashboard/DeviceInfoReadDto';
import DeviceNotificationsReadDto from '../../../../../Models/Dashboard/DeviceNotificationsReadDto';

interface Props {
  serialNumber: string;
  gatewayId: string;
  /** Parent asset display name, prefixed in the window title. */
  assetName?: string;
  onClose: () => void;
}

export default function GatewayTimelineDialog({ serialNumber, gatewayId, assetName, onClose }: Props) {
  const localizationService = useLocalization();
  const windowRef = useRef<any>(null);
  const [windowHeight, setWindowHeight] = useState<number | null>(null);

  const [eventFilter, setEventFilter] = useState<EventFilter>({
    startDate: moment().subtract(7, 'days').startOf('day').toDate(),
    endDate: moment().endOf('day').toDate(),
  });

  const deviceInfoQuery = useQuery<DeviceInfoReadDto | DeviceInfoReadDto[]>(
    [ReactQueryKeys.DeviceInfoQuery, serialNumber, eventFilter],
    () => healthDashboardApiService.getDeviceInfoChart(eventFilter, serialNumber),
    { enabled: !!serialNumber, keepPreviousData: true }
  );

  const deviceNotificationsQuery = useQuery<DeviceNotificationsReadDto>(
    [ReactQueryKeys.DeviceNotificationsChartQuery, serialNumber, eventFilter],
    () => healthDashboardApiService.getDeviceNotificationsChart(serialNumber, eventFilter),
    { enabled: !!serialNumber, keepPreviousData: true }
  );

  const deviceInfo = deviceInfoQuery.data;
  const deviceInfoArray = Array.isArray(deviceInfo) ? deviceInfo : deviceInfo ? [deviceInfo] : [];

  useEffect(() => {
    const windowElement = windowRef.current?.element;
    if (!windowElement) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWindowHeight(entry.contentRect.height);
      }
    });
    resizeObserver.observe(windowElement);
    setWindowHeight(windowElement.offsetHeight);
    return () => resizeObserver.disconnect();
  }, []);

  const handleStartDateChange = (e: any) => {
    if (e.value) {
      setEventFilter(prev => ({
        ...prev,
        startDate: moment(e.value).startOf('day').toDate(),
      }));
    }
  };

  const handleEndDateChange = (e: any) => {
    if (e.value) {
      setEventFilter(prev => ({
        ...prev,
        endDate: moment(e.value).endOf('day').toDate(),
      }));
    }
  };

  const chartHeight = windowHeight ? windowHeight - 200 : 450;

  const gatewayTitle = `${localizationService.toLanguageString('custom.gateway', enMessages.custom.gateway)} - ${serialNumber}`;

  return (
    <Window
      ref={windowRef}
      title={assetName ? `${assetName} - ${gatewayTitle}` : gatewayTitle}
      onClose={onClose}
      style={{ top: "5vh", left: "5vw", width: "90vw", height: "90vh" }}
      modal={true}
      className="custom-fullscreen-window"
    >
      <div style={{ height: "80vh", display: "flex", flexDirection: "column" }}>
        <div className="flex justify-between mb-4">
          <ButtonGroup>
            <Button
              fillMode="solid"
              style={{
                borderRadius: "0px",
                backgroundColor: "transparent",
                borderColor: "transparent",
                borderBottom: "2px solid var(--kendo-color-primary)",
                color: "var(--kendo-color-primary)"
              }}
            >
              {localizationService.toLanguageString('scheduler.timelineViewTitle', enMessages.scheduler.timelineViewTitle)}
            </Button>
          </ButtonGroup>
          <div className="flex items-center gap-x-2">
            <DatePicker
              value={eventFilter.startDate}
              onChange={handleStartDateChange}
              max={eventFilter.endDate || undefined}
              width="150px"
            />
            <span className="text-gray-400">-</span>
            <DatePicker
              value={eventFilter.endDate}
              onChange={handleEndDateChange}
              min={eventFilter.startDate || undefined}
              max={moment().toDate()}
              width="150px"
            />
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {(deviceInfoQuery.isLoading || deviceNotificationsQuery.isLoading) ? (
            <div className="flex items-center justify-center h-full">
              <LoadingSpinner />
            </div>
          ) : (
          <GatewayHealthDetails
            deviceData={{ serialNumber }}
            eventFilter={eventFilter}
            setEventFilter={setEventFilter}
            chartParentHeight={chartHeight}
            selectedEndpointId={gatewayId}
            deviceInfo={deviceInfoArray}
            deviceNotifications={deviceNotificationsQuery.data}
          />
          )}
        </div>
      </div>
    </Window>
  );
}
